import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const content = {
  'zh-TW': {
    category: '企業 AI 架構',
    title: '私隱優先架構：企業 AI 時代的設計哲學',
    desc: '當最敏感的合約與貿易單據交給 AI 處理，「分析後不儲存於伺服器」為何是企業最在意的安全承諾。',
    date: '2026 年 7 月',
    s1_title: '企業 AI 的信任悖論',
    s1: '每一間想導入 AI 的企業，都面對同一個矛盾：最需要 AI 處理的，往往是最敏感的文件 — 合約、信用證、財務單據；但最不敢交給 AI 的，也正是這些文件。資料會被保留多久？會否被用於訓練模型？會否在某次資安事故中外洩？只要其中一題無法給出確定答案，導入就會停在合規部門的桌上。',
    s2_title: '私隱優先如何運作',
    s2: '私隱優先架構給出清晰的答案：LC Shield：敏感文件僅在加密通道中傳輸、在記憶體中即時解析，分析後不會儲存於我們的伺服器，亦不用於模型訓練。Vowpace 會在您的帳戶存續期間儲存經遮蔽處理的合約文字。對企業而言，這是一套可被清楚說明與審視的私隱設計。',
    s3_title: '由理念到產品預設',
    s3: '在 DZent，私隱優先是整體設計原則：LC Shield 不會在分析後保留上傳文件，詳見 LC Shield 私隱政策（getlcshield.com/privacy）；Vowpace 會在您的帳戶存續期間儲存經遮蔽處理的合約文字，詳見 Vowpace 私隱政策（vowpace.com/privacy）。我們相信，企業級 AI 的競爭終局不只是模型能力，更是值得託付的架構誠信。',
    cta_title: '了解我們的企業級 AI 產品',
    cta_btn: '探索產品',
    back: '返回洞察中心',
  },
  'zh-CN': {
    category: '企业 AI 架构',
    title: '隐私优先架构：企业 AI 时代的设计哲学',
    desc: '当最敏感的合同与贸易单据交给 AI 处理，「分析后不存储于服务器」为何是企业最在意的安全承诺。',
    date: '2026 年 7 月',
    s1_title: '企业 AI 的信任悖论',
    s1: '每一间想导入 AI 的企业，都面对同一个矛盾：最需要 AI 处理的，往往是最敏感的文件 — 合同、信用证、财务单据；但最不敢交给 AI 的，也正是这些文件。资料会被保留多久？会否被用于训练模型？会否在某次网络安全事故中外泄？只要其中一题无法给出确定答案，导入就会停在合规部门的桌上。',
    s2_title: '隐私优先如何运作',
    s2: '隐私优先架构给出清晰的答案：LC Shield：敏感文件仅在加密通道中传输、在内存中即时解析，分析后不会存储于我们的服务器，亦不用于模型训练。Vowpace 会在您的账户存续期间存储经脱敏处理的合同文本。对企业而言，这是一套可被清楚说明与审视的隐私设计。',
    s3_title: '由理念到产品默认',
    s3: '在 DZent，隐私优先是整体设计原则：LC Shield 不会在分析后保留上传文件，详见 LC Shield 隐私政策（getlcshield.com/privacy）；Vowpace 会在您的账户存续期间存储经脱敏处理的合同文本，详见 Vowpace 隐私政策（vowpace.com/privacy）。我们相信，企业级 AI 的竞争终局不只是模型能力，更是值得托付的架构诚信。',
    cta_title: '了解我们的企业级 AI 产品',
    cta_btn: '探索产品',
    back: '返回洞察中心',
  },
  en: {
    category: 'Enterprise AI Architecture',
    title: 'Privacy-First Architecture: A Design Philosophy for the Enterprise AI Era',
    desc: 'When your most sensitive contracts and trade documents meet AI, why "not saved to our servers after analysis" is the promise enterprises care about most.',
    date: 'July 2026',
    s1_title: 'The enterprise AI trust paradox',
    s1: 'Every enterprise considering AI faces the same contradiction: the documents that need AI the most — contracts, letters of credit, financial instruments — are exactly the ones they dare not hand over. How long is data retained? Will it train someone\'s model? Could it leak in the next breach? If any of these questions lacks a definitive answer, adoption stalls on the compliance desk.',
    s2_title: 'How privacy-first processing works',
    s2: 'A privacy-first architecture gives a clear answer: sensitive documents travel through encrypted channels and are parsed in memory in real time. LC Shield: uploaded documents are not saved to our servers after analysis, nor used for model training. Vowpace stores redacted contract text for the life of your account. For enterprises, this is a privacy design that can be clearly explained and reviewed.',
    s3_title: 'From philosophy to product default',
    s3: 'At DZent, privacy-first is a design principle across our line: LC Shield does not retain uploaded documents after analysis; see the LC Shield Privacy Policy (getlcshield.com/privacy). Vowpace stores redacted contract text for the life of your account; see the Vowpace Privacy Policy (vowpace.com/privacy). We believe the endgame of enterprise AI is not just model capability, but architectural integrity worth trusting.',
    cta_title: 'Explore our enterprise AI products',
    cta_btn: 'Explore Products',
    back: 'Back to Insights',
  },
};

export default function InsightZeroStoragePage() {
  const { lang } = useLanguage();
  const c = content[lang] || content.en;

  return (
    <div data-testid="insight-zero-storage-page" className="pt-20 bg-[#080B10]">
      <SEOHead path="/insights/zero-storage" />
      <section className="py-20 md:py-28 bg-[#0E131F]/50 border-b border-slate-800/60">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <span className="inline-block px-3 py-1 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/30 text-xs font-bold text-[#6366F1] tracking-wide mb-6">
            {c.category}
          </span>
          <h1
            data-testid="insight-title"
            className="text-3xl md:text-4xl lg:text-5xl tracking-tighter font-bold text-white leading-tight mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}
          >
            {c.title}
          </h1>
          <p className="text-base md:text-lg text-[#94A3B8] leading-relaxed mb-6" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
            {c.desc}
          </p>
          <p className="text-sm text-white/70">{c.date} · DZent Limited</p>
        </div>
      </section>

      <article className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {[['s1_title', 's1'], ['s2_title', 's2'], ['s3_title', 's3']].map(([ht, bt]) => (
            <section key={ht} className="mb-14">
              <h2 className="text-2xl md:text-3xl tracking-tight font-bold text-[#F8FAFC] mb-5"
                  style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}>
                {c[ht]}
              </h2>
              <p className="text-base md:text-lg text-[#94A3B8] leading-relaxed" style={{ fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" }}>
                {c[bt]}
              </p>
            </section>
          ))}

          <section className="text-center py-12 border-t border-white/10">
            <h2 className="text-2xl md:text-3xl tracking-tight font-bold text-[#F8FAFC] mb-6"
                style={{ fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" }}>
              {c.cta_title}
            </h2>
            <Link to="/products">
              <Button data-testid="insight-cta-button"
                      className="h-12 px-8 rounded-full bg-[#6366F1] text-white font-bold text-sm hover:bg-white hover:text-[#080B10] transition-all">
                {c.cta_btn}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </section>

          <div className="pt-8">
            <Link to="/insights" data-testid="insight-back-link"
                  className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-white transition-colors">
              <ChevronLeft className="h-4 w-4" />
              {c.back}
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
